import { useState, useEffect, useCallback } from "react";
import {
  Avatar,
  Box,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DarkModeOutlined, LightModeOutlined } from "@mui/icons-material";
import { useContext } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import { ColorModeContext, tokens } from "../../../theme";
import GlobalSearchBar from "../../../components/common/GlobalSearchBar";
import { UserIcon } from "./icons/UserIcon";
import { User } from "../../../store/slice/AuthSlice";
import PendingSiteVisitWidget from "./PendingSiteVisitWidget";
import CreateBookingForm from "./CreateBookingForm";
import MemoryBookSummary from "./MemoryBookSummary";
import UpcomingFeatures from "./FeaturesUpcoming";
import UpcomingAudits from "./UpcomingAudits";
import MemoryBookMinimisedPill from "./MemoryBookMinimisedPill";
import UpcomingFeaturesMinimisedPill from "./UpcomingFeaturesMinimized";
import UpcomingAuditsMinimisedPill from "./UpcomingAuditsMinimizedPill";
import UpcomingJobCard from "./UpcomingJobCard";
import { fetchMemoryBookWidget } from "../service/memoryBookWidget.api";
import { fetchFeatureCounts } from "./FeaturesUpcoming";
import { fetchAuditCounts } from "./UpcomingAudits";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";

type ActivePanel =
  | "memoryBook"
  | "upcomingFeatures"
  | "upcomingAudits"
  | "jobCard";

const AppHomeContent = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const { hasPermission } = usePermissions();

  const canAddBooking = hasPermission(PERMISSIONS.BOOKING_ADD);
  const canSeeMemoryBook = hasPermission(
    PERMISSIONS.MEMORY_BOOK_ASSIGNMENT_VIEW,
  );
  const canSeeSiteVisit = hasPermission(PERMISSIONS.SITE_VISIT_VIEW);

  const user = useSelector(
    (state: RootState) => state.auth.user,
  ) as User | null;
  const users = useSelector((state: RootState) => state.users.users);
  const userDetails = users.find((u) => u.id === user?.id);

  const [activePanel, setActivePanel] = useState<ActivePanel>(
    canSeeMemoryBook ? "memoryBook" : "upcomingFeatures",
  );

  const [, setJobcardCounts] = useState({
    overdue: 0,
    inProgress: 0,
    needsReview: 0,
  });

  const [overdueCount, setOverdueCount] = useState(0);
  const [inDangerCount, setInDangerCount] = useState(0);

  const [featureCounts, setFeatureCounts] = useState({
    inProgress: 0,
    complete: 0,
    overdue: 0,
  });

  const [auditCounts, setAuditCounts] = useState({
    overdue: 0,
    inDanger: 0,
    safe: 0,
  });

  const loadCounts = useCallback(async () => {
    if (!canSeeMemoryBook) return;
    try {
      const res = await fetchMemoryBookWidget();
      const assignmentCounts = res.data.counts?.assignments;
      setOverdueCount(assignmentCounts?.overdue ?? 0);
      setInDangerCount(assignmentCounts?.in_danger ?? 0);
    } catch (err) {
      console.error("Failed to fetch memory book counts:", err);
    }
  }, [canSeeMemoryBook]);

  const loadDashboardCounts = useCallback(async () => {
    try {
      const [featureCounts, auditCounts] = await Promise.all([
        fetchFeatureCounts(),
        fetchAuditCounts(),
      ]);
      setFeatureCounts(featureCounts);
      setAuditCounts(auditCounts);
    } catch (err) {
      console.error("Failed to load dashboard counts", err);
    }
  }, []);

  useEffect(() => {
    loadCounts();
    loadDashboardCounts();
  }, [loadCounts, loadDashboardCounts]);

  const getCurrentDate = () => {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const now = new Date();
    return `${days[now.getDay()]}, ${months[now.getMonth()]} ${now.getDate()}, ${now.getFullYear()}`;
  };

  // Navigation helpers that skip memoryBook when user can't see it
  const nextAfterMemoryBook: ActivePanel = "upcomingFeatures";
  const prevBeforeUpcomingFeatures: ActivePanel = canSeeMemoryBook
    ? "memoryBook"
    : "upcomingAudits";
  const nextAfterUpcomingAudits: ActivePanel = canSeeMemoryBook
    ? "memoryBook"
    : "upcomingFeatures";

  return (
    <Box
      sx={{
        width: { xs: "100%", md: "85%" },
        mx: "auto",
        minHeight: "100vh",
        backgroundColor: "transparent",
        py: { xs: 2, md: 0 },
        px: { xs: 1, md: 4 },
        transform: { md: "scale(0.88)" },
        transformOrigin: { md: "top center" },
        transition: "transform 0.3s ease",
        position: "relative",
      }}
    >
      {canSeeSiteVisit && (
        <Box
          sx={{
            position: "absolute",
            top: { xs: 20, xl: 40 },
            right: { xs: 0, md: 0, lg: -170 },
            display: { xs: "none", lg: "block" },
            zIndex: 2,
          }}
        >
          <PendingSiteVisitWidget />
        </Box>
      )}

      {/* Welcome Section */}
      <Box
        sx={{
          textAlign: "center",
          mb: { xs: 4, md: 4 },
          mt: { xs: 0, md: 2 },
          maxWidth: "1200px",
          mx: "auto",
        }}
      >
        <Box
          sx={{ display: "inline-flex", alignItems: "center", gap: 2, mb: 2 }}
        >
          <Typography
            variant="h1"
            sx={{
              fontWeight: "bold",
              color: colors.primary[700],
              fontSize: { xs: "3rem", md: "4rem" },
            }}
          >
            Hi
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              borderRadius: "50px",
              pr: 3,
              pl: 1,
              py: 1,
              border: `1px solid ${colors.primary[700]}`,
            }}
          >
            <Avatar
              src={userDetails?.profile_picture_url || undefined}
              sx={{
                width: 44,
                height: 44,
                backgroundColor: colors.primary[400],
              }}
            >
              {!userDetails?.profile_picture_url && (
                <UserIcon sx={{ color: "white" }} />
              )}
            </Avatar>
            <Box sx={{ textAlign: "left" }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: colors.primary[100],
                  fontSize: "0.95rem",
                  lineHeight: 1.2,
                }}
              >
                {user?.first_name} {user?.last_name}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: colors.primary[700],
                  fontSize: "0.75rem",
                  textTransform: "capitalize",
                }}
              >
                {userDetails?.role || "User"}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Typography
          variant="body2"
          sx={{ color: colors.primary[700], fontSize: "0.9rem" }}
        >
          {`Today's date is ${getCurrentDate()}`}
        </Typography>
      </Box>

      {/* Global Search Bar */}
      <Box
        sx={{
          maxWidth: "620px",
          mx: "auto",
          mb: 4,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box sx={{ flex: 1, position: "relative" }}>
          <GlobalSearchBar placeholder="Search bookings, streams, venues, contacts…" />
        </Box>
        <Tooltip
          title={theme.palette.mode === "dark" ? "Light Mode" : "Dark Mode"}
          arrow
        >
          <IconButton
            onClick={colorMode.toggleColorMode}
            sx={{
              color: colors.primary[700],
              border: `1px solid ${colors.primary[300]}`,
              borderRadius: "12px",
              width: 44,
              height: 44,
            }}
          >
            {theme.palette.mode === "dark" ? (
              <LightModeOutlined />
            ) : (
              <DarkModeOutlined />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {canAddBooking && (
        <Box sx={{ maxWidth: "1000px", mx: "auto", mb: 2 }}>
          <CreateBookingForm
            buttonColor={
              !userDetails?.profile_picture_url
                ? colors.primary[400]
                : undefined
            }
          />
        </Box>
      )}

      {/* Panels + Pills */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          mb: { xs: 4, md: 6 },
        }}
      >
        {canSeeMemoryBook && activePanel === "memoryBook" && (
          <MemoryBookSummary
            expanded={true}
            onMinimise={() => setActivePanel(nextAfterMemoryBook)}
            onNext={() => setActivePanel(nextAfterMemoryBook)}
            onPrev={() => {}}
          />
        )}

        {activePanel === "upcomingFeatures" && (
          <UpcomingFeatures
            expanded={true}
            onMinimise={() => setActivePanel("upcomingAudits")}
            onNext={() => setActivePanel("upcomingAudits")}
            onPrev={() => setActivePanel(prevBeforeUpcomingFeatures)}
            onCountsChange={setFeatureCounts}
          />
        )}

        {activePanel === "upcomingAudits" && (
          <UpcomingAudits
            expanded={true}
            onMinimise={() => setActivePanel(nextAfterUpcomingAudits)}
            onNext={() => {}}
            onPrev={() => setActivePanel("upcomingFeatures")}
            onCountsChange={setAuditCounts}
          />
        )}

        {activePanel === "jobCard" && (
          <UpcomingJobCard
            expanded={true}
            onMinimise={() => setActivePanel("upcomingFeatures")}
            onNext={() => setActivePanel("upcomingFeatures")}
            onPrev={() => setActivePanel("upcomingAudits")}
            onCountsChange={setJobcardCounts}
          />
        )}

        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          {canSeeMemoryBook && activePanel !== "memoryBook" && (
            <MemoryBookMinimisedPill
              overdueCount={overdueCount}
              inDangerCount={inDangerCount}
              onExpand={() => setActivePanel("memoryBook")}
            />
          )}
          {activePanel !== "jobCard" && (
            <UpcomingJobCard
              expanded={false}
              onMinimise={() => setActivePanel("jobCard")}
              onCountsChange={setJobcardCounts}
            />
          )}
          {activePanel !== "upcomingFeatures" && (
            <UpcomingFeaturesMinimisedPill
              inProgressCount={featureCounts.inProgress}
              completeCount={featureCounts.complete}
              overdueCount={featureCounts.overdue}
              onExpand={() => setActivePanel("upcomingFeatures")}
            />
          )}
          {activePanel !== "upcomingAudits" && (
            <UpcomingAuditsMinimisedPill
              overdueCount={auditCounts.overdue}
              inDangerCount={auditCounts.inDanger}
              onExpand={() => setActivePanel("upcomingAudits")}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AppHomeContent;
